

import * as leaflet from "leaflet";
window.L = leaflet;

import omnivore from "leaflet-omnivore/index.js";
window.omnivore = omnivore;

// import "leaflet.smoothmarkerbouncing"	// Not needed since the use of Gleo bouncified sprites

import { patchEsriFeatureGrid, patchEsriRasterOrphan, patchEsriDeferredOnAdd, buildArcgisOverlay, resolveOverlayOpacity } from "./tracking/arcgis-feature-layer.mjs";
import { arcgisUrlShape, classifyArcgisResponse, arcgisServiceKind, resolveRenderModeOptions, zoomRangeForSelection, chooseServiceFitBounds, serviceNameFromUrl } from "./tracking/arcgis-service.mjs";
import { loadArcgisLegendHtml, setLegendSection, removeLegendSection } from "./tracking/arcgis-legend.mjs";
import { registerIdentifyLayer, unregisterIdentifyLayer } from "./tracking/arcgis-identify.mjs";
import { parseWmsCapabilities } from "./tracking/wms-capabilities.mjs";
import { parseIconHrefs, parseGroundOverlays, resolveLayerHref, deriveLayerBaseUrl } from "./tracking/kml-overlays.mjs";
import { parseKmlDocument, parseKmlStyles, attachKmlIconStyles, buildKmlGeoJsonOptions, buildKmlGroundOverlays } from "./tracking/kml-renderer.mjs";
import { buildFeaturePropertiesPopup, initFeaturePropertiesDataTable, destroyFeaturePropertiesDataTable } from "./tracking/feature-properties-popup.mjs";
import { parseCoordinate, formatCoordinate } from "./tracking/coordinate-format.mjs";

// Renders a stored ArcGIS Feature Service layer (root URL + selected-id CSV) onto an existing
// Leaflet map for the MapLayer admin preview, honoring the configured min/max zoom. Resolves to the
// added overlay (or null if nothing resolved). Consumed by Views/Settings/MapLayer.cshtml initMap().
window.GSatArcgisPreview = {
	// Header labels for the feature popup table; the cshtml overrides with localized strings.
	labels: { property: "Property", value: "Value" },
	// The track map's popupopen/popupclose DataTable handlers, shared so the preview popup
	// behaves identically (the cshtml wires them onto its own Leaflet map).
	initFeaturePropertiesDataTable: initFeaturePropertiesDataTable,
	destroyFeaturePropertiesDataTable: destroyFeaturePropertiesDataTable,
	// The FeatureServer URL-shape rule (root vs .../FeatureServer/{n} sublayer + canonicalization),
	// shared so the cshtml picker/save-gate/auto-load don't re-encode it as inline regexes.
	urlShape: arcgisUrlShape,
	// URL-derived default layer name ("USA_Wildfires_v1" -> "USA Wildfires v1") — the Name-seed
	// fallback when the service publishes no title (pure, unit-tested in arcgis-service.test.mjs).
	serviceNameFromUrl: serviceNameFromUrl,
	// Classifies the picker's ?f=json responses (service root / single layer / directory /
	// ArcGIS error), shared so the cshtml doesn't parse ArcGIS documents inline.
	classifyResponse: classifyArcgisResponse,
	// URL-kind + capability-matrix helpers for the picker's render-mode radios.
	serviceKind: arcgisServiceKind,
	renderModeOptions: resolveRenderModeOptions,
	// Scale-range -> zoom-window and extent -> fitBounds conversions for the picker's
	// auto-seed (pure, unit-tested in arcgis-service.test.mjs).
	zoomRangeForSelection: zoomRangeForSelection,
	// Preview-fit bounds from service metadata: the data-driven fullExtent when usable, else
	// the author's initialExtent; null when neither is (caller keeps the feature fit).
	serviceFitBounds: chooseServiceFitBounds,
	// Legend probe + per-map control, shared with the track map's implementation.
	loadLegendHtml: loadArcgisLegendHtml,
	setLegendSection: setLegendSection,
	removeLegendSection: removeLegendSection,
	// Merged identify popup, shared with the track map. The cshtml registers the preview overlay
	// AFTER its own staleness check (never from addLayers — a stale build must not win the key).
	registerIdentify: registerIdentifyLayer,
	unregisterIdentify: unregisterIdentifyLayer,
	addLayers: function (map, url, layersCsv, minZoom, maxZoom, renderMode, filter, opacityPercent) {
		patchEsriFeatureGrid();
		patchEsriRasterOrphan();
		patchEsriDeferredOnAdd();
		var params = {
			renderMode: renderMode === "image" ? "image" : "features",
			filter: filter == null || filter === "" ? null : filter,
		};
		var popupOptions = { maxWidth: 480, minWidth: 340, maxHeight: 320 };
		return buildArcgisOverlay(url, layersCsv, params, {
			minZoom: minZoom,
			maxZoom: maxZoom,
			// The form's Opacity field is a 0-100 percent; only image mode consumes it.
			opacity: resolveOverlayOpacity(params.renderMode, opacityPercent == null ? null : opacityPercent / 100),
			log: function (message) {
				if (window.console && window.console.warn) {
					window.console.warn(message);
				}
			},
			bindPopup: function (featureLayer, presentation) {
				featureLayer.bindPopup(function (fl) {
					return buildFeaturePropertiesPopup(fl.feature.properties, window.GSatArcgisPreview.labels, presentation);
				}, popupOptions);
			},
		}).then(function (overlay) {
			if (overlay != null) {
				overlay.addTo(map);
			}
			return overlay;
		});
	},
};

// Fetches a WMS service's GetCapabilities and returns its requestable layers. Rejects on
// network/CORS/parse failure (the admin form then shows a manual-entry hint). Consumed by
// Views/Settings/MapLayer.cshtml's WMS picker.
window.GSatWms = {
	fetchLayers: function (url) {
		var base = String(url || "").split("?")[0].replace(/\/+$/, "");
		var capUrl = base + "?SERVICE=WMS&REQUEST=GetCapabilities&VERSION=1.3.0";
		return fetch(capUrl, { headers: { Accept: "application/xml, text/xml" } })
			.then(function (response) {
				// fetch only rejects on network/CORS failure, not HTTP errors. Without this an error
				// body (HTML/text) would parse to zero layers and show the misleading "no layers found"
				// instead of the "load failed" hint, masking a real service/auth/URL problem.
				if (!response.ok) {
					throw new Error("WMS GetCapabilities failed: HTTP " + response.status);
				}
				return response.text();
			})
			.then(function (text) {
				return parseWmsCapabilities(text);
			});
	},
};

// KML/KMZ parsing + rendering helpers shared with the MapLayer admin preview
// (Views/Settings/MapLayer.cshtml), so its inline script renders styles, popups, icons and
// GroundOverlays exactly like the track map does (map-base.js imports the same modules).
window.GSatKmlOverlays = {
	parseIconHrefs: parseIconHrefs,
	parseGroundOverlays: parseGroundOverlays,
	resolveLayerHref: resolveLayerHref,
	deriveLayerBaseUrl: deriveLayerBaseUrl,
	parseKmlDocument: parseKmlDocument,
	parseKmlStyles: parseKmlStyles,
	attachKmlIconStyles: attachKmlIconStyles,
	buildKmlGeoJsonOptions: buildKmlGeoJsonOptions,
	buildKmlGroundOverlays: buildKmlGroundOverlays,
};

// CurrentCulture <-> parsed-number conversion for coordinate input fields, shared so every JS<->
// input-field crossing on the MapLayer admin form's Bounds fields (Views/Settings/MapLayer.cshtml)
// applies the identical rules -- both directions previously drifted independently (parsing was
// fixed and re-fixed; writing was never touched, leaving a live bug for non-period-decimal
// locales). Consumed by Views/Settings/MapLayer.cshtml's setBounds(), tilePreviewReadBounds(), and
// the manual Bounds-field change handler.
window.GSatCoordinateFormat = {
	parse: parseCoordinate,
	format: formatCoordinate,
};